import { BASE_URI_LC, BASE_URI_PROGRESS } from '../../constants/ApiConstants';
import http from 'hub-http/clients/apiClient';
import { registerMutation, registerQuery, useMutation, useQuery } from 'academy-common-lib/utils/DataFetchingClient';
import { deleteContentProgressFromCache, useSetContentProgressStatusCache } from '../userProgress';
import { useFullLesson } from '../lessons/lessonQueries';
import { useCallback, useMemo } from 'react';
const EXERCISE_SUBMISSIONS_URI = `${BASE_URI_PROGRESS}/exercise-submissions`;
const GET_PRACTICAL_EXERCISE_REQUIREMENTS = registerQuery({
  fieldName: 'practicalExerciseRequirements',
  args: ['contentId'],
  fetcher({
    contentId
  }) {
    return http.get(`${BASE_URI_LC}/exercises/${contentId}/requirements`);
  }
});
export const usePracticalExerciseRequirements = contentId => {
  const {
    data: practicalExerciseRequirements,
    loading: practicalExerciseRequirementsLoading,
    error: practicalExerciseRequirementsError
  } = useQuery(GET_PRACTICAL_EXERCISE_REQUIREMENTS, {
    variables: {
      contentId
    }
  });
  return {
    practicalExerciseRequirements,
    practicalExerciseRequirementsLoading,
    practicalExerciseRequirementsError
  };
};
export const usePracticalExercise = (lessonId, contentId, language) => {
  const {
    lesson,
    lessonLoading,
    lessonError
  } = useFullLesson(lessonId, language);
  const practicalExercise = useMemo(() => {
    if (!lesson || !lesson.content) return null;
    const foundExercise = lesson.content.find(content => content.id === contentId);
    return foundExercise;
  }, [contentId, lesson]);
  return {
    practicalExercise,
    practicalExerciseLoading: lessonLoading,
    practicalExerciseError: lessonError
  };
};
const START_PRACTICAL_EXERCISE = registerMutation({
  fieldName: 'setPracticalExerciseStarted',
  args: ['contentId', 'trackId', 'lessonId'],
  fetcher({
    contentId,
    lessonId,
    trackId
  }) {
    return http.post(`${BASE_URI_PROGRESS}/exercise-submissions/${contentId}/asset/start`, {
      data: {
        trackId,
        lessonId
      }
    });
  }
});
export const useSetPracticalExerciseStarted = language => {
  const [mutate, {
    loading: startPracticalExerciseLoading,
    called: startPracticalExerciseCalled,
    error: startPracticalExerciseError
  }] = useMutation(START_PRACTICAL_EXERCISE);
  const {
    setContentProgressStatusCache
  } = useSetContentProgressStatusCache({
    language
  });
  const startExercise = useCallback((trackId, lessonId, contentId) => {
    return mutate({
      variables: {
        contentId,
        lessonId,
        trackId
      },
      update() {
        setContentProgressStatusCache(contentId, 'STARTED', language);
      }
    }).then(result => result.data);
  }, [language, mutate, setContentProgressStatusCache]);
  return [startExercise, {
    startPracticalExerciseLoading,
    startPracticalExerciseCalled,
    startPracticalExerciseError
  }];
};
const DELETE_PRACTICAL_EXERCISE_PROGRESS = registerMutation({
  fieldName: 'deletePracticalExerciseProgress',
  args: ['contentId'],
  fetcher({
    contentId
  }) {
    return http.delete(`${BASE_URI_PROGRESS}/user-progress/exercise/${contentId}`);
  }
});
export const useDeletePracticalExerciseProgress = () => {
  const [mutate, {
    loading: deletePracticalExerciseProgressLoading,
    called: deletePracticalExerciseProgressCalled,
    error: deletePracticalExerciseProgressError
  }] = useMutation(DELETE_PRACTICAL_EXERCISE_PROGRESS);
  const deleteExerciseProgress = useCallback(contentId => {
    return mutate({
      variables: {
        contentId
      },
      update() {
        deleteContentProgressFromCache(contentId);
      }
    }).then(response => response.data);
  }, [mutate]);
  return [deleteExerciseProgress, {
    deletePracticalExerciseProgressLoading,
    deletePracticalExerciseProgressCalled,
    deletePracticalExerciseProgressError
  }];
};
const SUBMIT_MANUALLY_GRADED_PRACTICAL_EXERCISE = registerMutation({
  fieldName: 'submitManuallyGradedPracticalExercise',
  args: ['contentId', 'language', 'trackId', 'lessonId'],
  fetcher({
    contentId,
    language,
    trackId,
    lessonId
  }) {
    return http.post(`${BASE_URI_PROGRESS}/exercise-submission/${contentId}/asset/manual`, {
      data: {
        trackId,
        lessonId,
        language
      }
    });
  }
});
export const useSetManuallyGradedPracticalExerciseSubmitted = language => {
  const [mutate, {
    loading: submitManuallyGradedPracticalExerciseLoading,
    called: submitManuallyGradedPracticalExerciseCalled,
    error: submitManuallyGradedPracticalExerciseError
  }] = useMutation(SUBMIT_MANUALLY_GRADED_PRACTICAL_EXERCISE);
  const {
    setContentProgressStatusCache
  } = useSetContentProgressStatusCache({
    language
  });
  const submitManuallyGradedExercise = useCallback((contentId, currentLanguage, trackId, lessonId) => {
    return mutate({
      variables: {
        contentId,
        language: currentLanguage,
        trackId,
        lessonId
      },
      update() {
        setContentProgressStatusCache(contentId, 'IN_REVIEW', language);
      }
    }).then(response => response.data);
  }, [mutate, setContentProgressStatusCache, language]);
  return [submitManuallyGradedExercise, {
    submitManuallyGradedPracticalExerciseLoading,
    submitManuallyGradedPracticalExerciseCalled,
    submitManuallyGradedPracticalExerciseError
  }];
};
const SUBMIT_AUTOMATICALLY_GRADED_PRACTICAL_EXERCISE = registerMutation({
  fieldName: 'submitAutomaticallyGradedPracticalExercise',
  args: ['contentId'],
  fetcher({
    contentId
  }) {
    return http.post(`${EXERCISE_SUBMISSIONS_URI}/${contentId}/automatic`);
  }
});
export const useSetAutomaticallyGradedPracticalExerciseSubmitted = language => {
  const [mutate, {
    loading: submitAutomaticallyGradedPracticalExerciseLoading,
    called: submitAutomaticallyGradedPracticalExerciseCalled,
    error: submitAutomaticallyGradedPracticalExerciseError
  }] = useMutation(SUBMIT_AUTOMATICALLY_GRADED_PRACTICAL_EXERCISE);
  const {
    setContentProgressStatusCache
  } = useSetContentProgressStatusCache({
    language
  });
  const submitAutomaticallyGradedExercise = useCallback(contentId => {
    return mutate({
      variables: {
        contentId
      },
      update() {
        setContentProgressStatusCache(contentId, 'IN_REVIEW', language);
      }
    }).then(response => response.data);
  }, [mutate, setContentProgressStatusCache, language]);
  return [submitAutomaticallyGradedExercise, {
    submitAutomaticallyGradedPracticalExerciseLoading,
    submitAutomaticallyGradedPracticalExerciseCalled,
    submitAutomaticallyGradedPracticalExerciseError
  }];
};
const SUBMIT_MANUALLY_GRADED_PRACTICAL_EXERCISE_WITH_ASSET = registerMutation({
  fieldName: 'submitManuallyGradedPracticalExerciseWithAsset',
  args: ['contentId', 'assetType', 'reference'],
  fetcher({
    contentId,
    assetType,
    reference
  }) {
    return http.post(`${EXERCISE_SUBMISSIONS_URI}/${contentId}/manual-with-asset`, {
      data: {
        assetType,
        reference
      }
    });
  }
});
export const useSetManuallyGradedPracticalExerciseWithAssetSubmitted = language => {
  const [mutate, {
    loading: submitManuallyGradedPracticalExerciseWithAssetLoading,
    called: submitManuallyGradedPracticalExerciseWithAssetCalled,
    error: submitManuallyGradedPracticalExerciseWithAssetError
  }] = useMutation(SUBMIT_MANUALLY_GRADED_PRACTICAL_EXERCISE_WITH_ASSET);
  const {
    setContentProgressStatusCache
  } = useSetContentProgressStatusCache({
    language
  });
  const submitPracticalExerciseWithAsset = useCallback((contentId, assetType, reference) => {
    return mutate({
      variables: {
        contentId,
        assetType,
        reference
      },
      update() {
        setContentProgressStatusCache(contentId, 'IN_REVIEW', language);
      }
    }).then(response => response.data);
  }, [mutate, setContentProgressStatusCache, language]);
  return [submitPracticalExerciseWithAsset, {
    submitManuallyGradedPracticalExerciseWithAssetLoading,
    submitManuallyGradedPracticalExerciseWithAssetCalled,
    submitManuallyGradedPracticalExerciseWithAssetError
  }];
};